import {
  Search,
  X,
  ArrowRight,
  LucideUser,
  GlobeLock,
  LucideLogOut,
  FileText,
  Mail,
} from "lucide-react";

import {
  FaHome,
  FaShoppingBag,
  FaInfoCircle,
  FaRegUser,
  FaLock,
  FaTruck,
  FaUserCircle,
  FaSignOutAlt,
  FaUser,
  FaUnlockAlt,
  FaBoxes,
} from "react-icons/fa";
import QuoteRequestModal from "../Modals/QuoteModal";
import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import methodModel from "../../methods/methods";
import { useDispatch, useSelector } from "react-redux";
import {
  IoIosMenu,
  IoIosNotificationsOutline,
  IoMdUnlock,
} from "react-icons/io";
import { logout } from "../../actions/user";
import FloatingCart from "../Cart/Cart";
import { MdDashboard, MdOutlineDashboardCustomize } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import AIProductModal from "./AiProductModal/AIProductModal";
import { useAIModal } from "../../hooks/useAIModal";
import { BsBoxSeamFill } from "react-icons/bs";
import NotificationBell from "../NotificationBell/NotificationBell";
import { getTenantInfo } from "../../utils/domain";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DB_NAME = "ShopCartDB";
const STORE_NAME = "cart";
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getCartCount() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => {
        const items = req.result || [];
        const total = items.reduce(
          (sum, item) => sum + (item.quantity || 1),
          0,
        );
        resolve(total);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return 0;
  }
}

function SearchOverlay({ open, onClose }) {
  const Navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const debounceRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    ApiClient.get("equipment-type/listing", {
      page: 1,
      count: 100000,
      status: "active",
    })
      .then((res) => {
        if (res?.success) {
          const data = (res?.data?.data || [])
            .map((item) => item.title)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 6);
          setSuggestions(data);
        }
      })
      .catch(() => { });
  }, []);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setHasSearched(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const fetchProducts = (searchVal) => {
    if (!searchVal.trim()) {
      setResults([]);
      setHasSearched(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setHasSearched(true);
    ApiClient.get("product/browse", {
      search: searchVal,
      page: 1,
      count: 10,
      isAiGenerated: "0",
    })
      .then((res) => {
        setResults(res?.data?.products || []);
        setIsLoading(false);
      })
      .catch(() => {
        setResults([]);
        setIsLoading(false);
      });
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchProducts(val), 400);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setHasSearched(false);
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (text) => {
    setQuery(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchProducts(text);
  };

  const handleProductClick = (item) => {
    onClose();
    Navigate(`/products/detail?id=${item?.id || item?._id}`);
  };

  const handleViewAll = () => {
    onClose();
    Navigate(`/products?search=${encodeURIComponent(query)}`);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        style={{ animation: "fadeIn 0.2s ease" }}
        onClick={onClose}
      />
      <div
        className="fixed top-0 left-0 right-0 z-[99] bg-white shadow-2xl"
        style={{ animation: "slideDown 0.25s ease" }}
      >
        <div className="container mx-auto px-5">
          <div className="flex items-center gap-4 h-[60px] md:h-[70px]">
            <div className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] flex-shrink-0">
              <Search size={16} color="white" />
            </div>
            <input
              ref={inputRef}
              value={query}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim()) {
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  fetchProducts(query);
                }
              }}
              placeholder="Search products by name, description..."
              className="flex-1 text-base md:text-lg text-gray-800 placeholder-gray-400 border-none outline-none bg-transparent font-[400]"
            />
            {query && (
              <button
                onClick={handleClear}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  viewBox="0 0 14 14"
                  stroke="#9ca3af"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M1 1l12 12M13 1L1 13" />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0 ml-1"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>
          <div className="border-t border-gray-100" />
        </div>

        <div
          className="container mx-auto px-5 overflow-y-auto"
          style={{ maxHeight: "min(480px, 70vh)" }}
        >
          {isLoading && (
            <div className="py-4 space-y-1 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 px-3 py-3 rounded-xl"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-gray-100 rounded-full w-2/5" />
                    <div className="h-3 bg-gray-100 rounded-full w-3/5" />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-gray-100 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <div className="py-3">
              <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold px-3 mb-2">
                {results.length} result{results.length !== 1 ? "s" : ""} found
              </p>
              <div className="space-y-0.5">
                {results.map((item, idx) => (
                  <button
                    key={item._id || idx}
                    onClick={() => handleProductClick(item)}
                    className="w-full flex items-center gap-4 px-3 py-3 rounded-xl hover:bg-[#F0F6FC] transition-colors group text-left"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#F5F8FC] border border-[#EAF1F9] flex-shrink-0 flex items-center justify-center">
                      {item.image ? (
                        <img
                          src={methodModel.userImg(item.image)}
                          alt={item.productName}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <svg
                          width="22"
                          height="22"
                          fill="none"
                          viewBox="0 0 64 64"
                        >
                          <rect
                            x="8"
                            y="20"
                            width="48"
                            height="36"
                            rx="5"
                            fill="#EBF3FB"
                            stroke="#C8DCF0"
                            strokeWidth="2"
                          />
                          <circle
                            cx="32"
                            cy="36"
                            r="6"
                            fill="#D6E9F8"
                            stroke="#116DAE"
                            strokeWidth="1.5"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-[600] text-gray-800 truncate capitalize leading-snug group-hover:text-[#116DAE] transition-colors">
                        {item.productName}
                      </p>
                      <p
                        dangerouslySetInnerHTML={{
                          __html:
                            item?.description || "No description available",
                        }}
                        className="text-[12px] text-gray-400 truncate capitalize leading-relaxed mt-0.5"
                      ></p>
                      {item.equipmentType && (
                        <span className="inline-block mt-1 text-[10px] font-semibold text-[#115B9D] bg-[#EBF3FB] border border-[#D6E9F8] px-2 py-0.5 rounded-full leading-none">
                          {item.equipmentType}
                        </span>
                      )}
                    </div>
                    <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-transparent group-hover:bg-[#E2EDF7] transition-colors">
                      <ArrowRight
                        size={14}
                        className="text-gray-400 group-hover:text-[#116DAE] transition-colors"
                      />
                    </div>
                  </button>
                ))}
              </div>
              <div className="pt-3 pb-4 border-t border-gray-100 mt-3">
                <button
                  onClick={handleViewAll}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#C8DCF0] text-[#116DAE] text-sm font-semibold hover:bg-[#EBF3FB] transition-colors"
                >
                  View all results for "{query}"
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {!isLoading && hasSearched && results.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#F0F6FC] flex items-center justify-center">
                <Search size={24} className="text-[#C8DCF0]" />
              </div>
              <div className="text-center">
                <p className="text-[15px] font-semibold text-gray-700">
                  No products found
                </p>
                <p className="text-[13px] text-gray-400 mt-1">
                  No results for{" "}
                  <span className="font-semibold text-gray-600">"{query}"</span>
                  . Try a category below.
                </p>
              </div>
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mt-1">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestionClick(s)}
                      className="text-[12px] text-[#116DAE] bg-[#EBF3FB] hover:bg-[#D6E9F8] border border-[#C8DCF0] px-3 py-1.5 rounded-full transition-colors font-medium"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isLoading && !hasSearched && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <p className="text-[13px] text-gray-400">
                Start typing to search our product catalog
              </p>
              {suggestions.length > 0 && (
                <>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">
                    Browse by category
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSuggestionClick(s)}
                        className="text-[12px] text-[#116DAE] bg-[#EBF3FB] hover:bg-[#D6E9F8] border border-[#C8DCF0] px-3 py-1.5 rounded-full transition-colors font-medium"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideDown { from { transform: translateY(-16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        `}</style>
      </div>
    </>
  );
}

export default function Header() {
  const subDomain = "logistics.charlessmithassociates.com";
  // const subDomain = "localhost";
  const dispatch = useDispatch();
  const [isOpen1, setIsOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [cartCount, setCartCount] = React.useState(0);
  const { isOpen, openModal, closeModal } = useAIModal();
  const [detail, setDetail] = React.useState(null);
  const hostname = window.location.hostname;

  const [showBrowseInHeader, setShowBrowseInHeader] = React.useState(false);

  const GetUserDetail = () => {
    if (!user?._id) return;
    ApiClient.get(`user/profile?id=${user?._id || user?.id}`).then((res) => {
      if (res?.success) {
        setDetail(res?.user_data);
      }
    });
  };

  React.useEffect(() => {
    GetUserDetail();
    if (localStorage.getItem("__aipm_show_quote_after__")) {
      openModal();
    }
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const triggerHeight = 500;
      setShowBrowseInHeader(window.scrollY > triggerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const refreshCartCount = React.useCallback(() => {
    getCartCount()
      .then(setCartCount)
      .catch(() => setCartCount(0));
  }, []);

  React.useEffect(() => {
    // Load on mount
    refreshCartCount();

    // Re-read whenever ProductDetail or Cart fires "cart-updated"
    window.addEventListener("cart-updated", refreshCartCount);

    // Also re-read when tab regains focus (user switches tabs and comes back)
    window.addEventListener("focus", refreshCartCount);

    return () => {
      window.removeEventListener("cart-updated", refreshCartCount);
      window.removeEventListener("focus", refreshCartCount);
    };
  }, [refreshCartCount]);
  // ───────────────────────────────────────────────────────────────────────────

  const user = useSelector((state) => state.user);
  const WebDetails = useSelector((state) => state.logo);
  const tenantInfo = getTenantInfo();

  const isTenantAdminUser =
    user?.isTenantAdmin ||
    user?.role?.name === "Tenant-Admin" ||
    user?.role === "Tenant-Admin" ||
    user?.roleType === "tenant_admin" ||
    user?.role?.loginPannel === "admin";

  const currentPath = location.pathname;
  const isProductsPage = currentPath === "/products";

  React.useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserDisplayName = () => {
    return (
      user?.fullName ||
      (user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "") ||
      user?.name ||
      "Admin"
    );
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || "A";
  };

  const getUserRoleLabel = () => {
    if (isTenantAdminUser) return "Tenant Admin";
    if (user?.role?.name) return user.role.name;
    if (user?.role && typeof user.role === "string") return user.role;
    return "Member";
  };

  const navLinks = [
    {
      label: "Profile",
      icon: FaUser,
      href: "/profile",
      className: "lg:!hidden",
    },
    {
      label: "Dashboard",
      icon: MdDashboard,
      href: isTenantAdminUser ? "/tenant/dashboard" : "/dashboard",
      className: "lg:!hidden",
    },
    {
      label: "Customers",
      icon: FaUser,
      href: "/tenant/customers",
      className: "lg:!hidden",
    },
    {
      label: "Plans & Subscription",
      icon: FaShoppingBag,
      href: "/tenant/plans",
      className: "lg:!hidden",
    },
    { label: "Home", icon: FaHome, href: "/" },
    { label: "Browse Products", icon: BsBoxSeamFill, href: "/products" },
    { label: "About", icon: FaInfoCircle, href: "/about/us" },
    { label: "My Documents", icon: FaShoppingBag, href: "/orders" },
    { label: "Track Order", icon: FaTruck, href: "/track/orders" },
    {
      label: "Change Password",
      icon: FaUnlockAlt,
      href: "/changepassword",
      className: "lg:!hidden",
    },
  ];

  const isActive = (href) => href && currentPath === href;
  const Navigate = useNavigate();

  const Logout = () => {
    dispatch(logout());
    localStorage.removeItem("persist:admin-app");
    localStorage.removeItem("token");
    Navigate("/login");
  };

  const [isScrolled, setIsScrolled] = React.useState(false);
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div className="">
        <div
          className={`w-full fixed top-0 z-[99] transition-all duration-300 ${hostname === subDomain && isHomePage
            ? isScrolled
              ? "bg-[#0A2540] border-b border-[#375848]"
              : "bg-transparent"
            : "bg-white shadow-[0px_4px_20px_-6px_#d1d1d1]"
            }`}
        >
          <div className="container mx-auto px-5 flex items-center justify-between h-[60px] lg:h-[70px]">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={WebDetails?.logo || "/assets/img/logo.png"}
                alt={WebDetails?.portal_title || WebDetails?.company_name || "Logo"}
                className="h-9 sm:h-10 lg:h-11 w-auto max-w-[180px] sm:max-w-[220px] lg:max-w-[260px] object-contain object-left cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                onClick={() =>
                  Navigate(
                    isTenantAdminUser
                      ? "/tenant/dashboard"
                      : tenantInfo.isTenant
                      ? "/products"
                      : "/"
                  )
                }
              />
            </div>

            <div className="hidden lg:flex items-center gap-1.5 text-[14px] xl:text-[15px]">
              {navLinks
                .filter(({ label }) => {
                  if (isTenantAdminUser) {
                    if (
                      label === "Browse Products" ||
                      label === "Home" ||
                      label === "About" ||
                      label === "My Documents" ||
                      label === "Track Order"
                    ) {
                      return false;
                    }
                    if (
                      label === "Dashboard" ||
                      label === "Customers" ||
                      label === "Plans & Subscription"
                    ) {
                      return true;
                    }
                  } else if (tenantInfo.isTenant) {
                    if (label === "Home" || label === "About") {
                      return false;
                    }
                  }

                  if (!user?._id) {
                    if (label.includes("Dashboard") || label === "My Documents")
                      return false;
                  }

                  if (user?._id && label === "Track Order") return false;

                  if (label === "Browse Products") {
                    if (hostname === subDomain) {
                      return false;
                    }

                    if (isHomePage) {
                      return showBrowseInHeader;
                    }

                    return true;
                  }
                  if (
                    label.includes("Dashboard") ||
                    label === "Profile" ||
                    label === "Change Password"
                  )
                    return false;

                  return true;
                })
                .map(({ label, href }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => Navigate(href)}
                    className={`cursor-pointer transition-all duration-150 font-medium relative px-3.5 py-1.5 rounded-lg text-sm
          ${isActive(href)
                        ? hostname === subDomain && isHomePage
                          ? "bg-[#98C65B]/15 text-[#98C65B] font-semibold"
                          : "bg-blue-50/90 text-[#115B9D] font-semibold border border-blue-100"
                        : hostname === subDomain && isHomePage
                          ? "text-white/80 hover:text-white hover:bg-white/10"
                          : "text-slate-600 hover:text-[#115B9D] hover:bg-slate-50"
                      }`}
                  >
                    {label}
                  </button>
                ))}
              {!isTenantAdminUser && (
                <button
                  type="button"
                  onClick={() => openModal()}
                  className={`cursor-pointer transition-all duration-150 font-medium relative px-3.5 py-1.5 rounded-lg text-sm ${
                    hostname === subDomain && isHomePage
                      ? "text-white/80 hover:text-white hover:bg-white/10"
                      : "text-slate-600 hover:text-[#115B9D] hover:bg-slate-50"
                  }`}
                >
                  Quick Quote
                </button>
              )}
            </div>

            <div className="flex items-center gap-2.5 sm:gap-3">
              {user?._id && (
                <NotificationBell
                  colour={
                    hostname === subDomain && isHomePage && !isScrolled
                      ? "text-white"
                      : "text-slate-600"
                  }
                />
              )}
              {/* Search button */}
              {!isTenantAdminUser && !isProductsPage && (
                <button
                  onClick={() => setSearchOpen(true)}
                  className={`relative flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#115B9D]/20 active:scale-95 group ${
                    hostname === subDomain && isHomePage && !isScrolled
                      ? "border-white/20 bg-white/10 hover:bg-white/20 text-white"
                      : "border-slate-200/90 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-600 hover:text-[#115B9D]"
                  }`}
                  aria-label="Search"
                >
                  <Search size={18} className="transition-transform duration-200 group-hover:scale-110" />
                </button>
              )}

              {/* User Dropdown - Desktop */}
              {!(user?.id || user?._id) ? (
                <>
                  <Link to="/login">
                    <button
                      className={`${hostname === subDomain && isHomePage ? "bg-[#98C65A]" : "bg-gradient-to-br from-[#115B9D] to-[#116DAE]"} hidden lg:flex items-center gap-2 text-white rounded-full text-sm shadow hover:scale-[1.04] duration-300 transition px-5 py-2`}
                    >
                      Login
                    </button>
                  </Link>
                  <Link to="/signup">
                    <button
                      className={`${hostname === subDomain && isHomePage ? "text-white" : "text-gray-600"} hidden lg:flex items-center gap-2  border rounded-full text-sm shadow hover:scale-[1.04] duration-300 transition px-5 py-2`}
                    >
                      Signup
                    </button>
                  </Link>
                </>
              ) : (
                <div className="max-lg:hidden relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`inline-flex items-center gap-2.5 p-1.5 pr-3 pl-1.5 rounded-full border transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#115B9D]/20 group ${
                      dropdownOpen
                        ? "border-[#115B9D]/50 bg-blue-50/50 shadow-md ring-2 ring-[#115B9D]/15"
                        : hostname === subDomain && isHomePage && !isScrolled
                        ? "border-white/20 bg-white/10 hover:bg-white/20 text-white"
                        : "border-slate-200/90 bg-white hover:bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    <div className="relative">
                      {user?.image && !user.image.includes("placeholder") ? (
                        <img
                          alt="Profile"
                          src={methodModel.userImg(user?.image)}
                          className="h-9 w-9 rounded-full object-cover ring-2 ring-white/90 shadow-sm"
                          onError={(e) => {
                            e.target.style.display = "none";
                            if (e.target.nextElementSibling) {
                              e.target.nextElementSibling.style.display = "flex";
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={`h-9 w-9 rounded-full bg-gradient-to-tr from-[#115B9D] via-[#1670b8] to-[#2590e0] text-white font-bold text-xs items-center justify-center ring-2 ring-white/90 shadow-sm uppercase tracking-wider ${
                          user?.image && !user.image.includes("placeholder") ? "hidden" : "flex"
                        }`}
                      >
                        {getUserInitials()}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white" />
                    </div>

                    <div className="text-left min-w-0 pr-0.5">
                      <h2
                        className={`font-semibold text-xs sm:text-[13px] leading-tight truncate max-w-[120px] xl:max-w-[150px] ${
                          hostname === subDomain && isHomePage && !isScrolled ? "text-white" : "text-slate-800"
                        }`}
                      >
                        {getUserDisplayName()}
                      </h2>
                      <p
                        className={`text-[10px] sm:text-[11px] font-medium leading-tight truncate ${
                          hostname === subDomain && isHomePage && !isScrolled ? "text-white/75" : "text-slate-400"
                        }`}
                      >
                        {getUserRoleLabel()}
                      </p>
                    </div>

                    <IoChevronDown
                      className={`text-sm transition-transform duration-200 ${
                        dropdownOpen ? "rotate-180 text-[#115B9D]" : "text-slate-400 group-hover:text-slate-600"
                      }`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute right-0 z-[9999] mt-2.5 w-64 origin-top-right rounded-2xl bg-white/98 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.12),0_4px_16px_rgba(0,0,0,0.06)] border border-slate-100 ring-1 ring-black/5 focus:outline-none overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                      {/* User Info Header inside Menu */}
                      <div className="px-4 py-3.5 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100 flex items-center gap-3">
                        <div className="relative shrink-0">
                          {user?.image && !user.image.includes("placeholder") ? (
                            <img
                              alt="Profile"
                              src={methodModel.userImg(user?.image)}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-[#115B9D] to-[#2590e0] text-white font-bold text-xs flex items-center justify-center ring-2 ring-white shadow-sm uppercase">
                              {getUserInitials()}
                            </div>
                          )}
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-[13px] font-bold text-slate-800 truncate">
                            {getUserDisplayName()}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {user?.email || "Account Active"}
                          </p>
                          <span className="inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[9px] font-bold bg-[#115B9D]/10 text-[#115B9D] uppercase tracking-wide">
                            {getUserRoleLabel()}
                          </span>
                        </div>
                      </div>

                      {/* Dropdown Links */}
                      <div className="p-1.5 space-y-0.5">
                        <Link
                          to={isTenantAdminUser ? "/tenant/dashboard" : "/dashboard"}
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-xs sm:text-sm text-slate-700 hover:text-[#115B9D] hover:bg-blue-50/70 rounded-xl transition-all group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] flex items-center justify-center shrink-0 group-hover:bg-[#115B9D] group-hover:text-white transition-colors">
                            <MdOutlineDashboardCustomize className="h-4 w-4" />
                          </div>
                          <span className="font-medium">Dashboard</span>
                        </Link>

                        <Link
                          to="/profile"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-xs sm:text-sm text-slate-700 hover:text-[#115B9D] hover:bg-blue-50/70 rounded-xl transition-all group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                            <LucideUser className="h-4 w-4" />
                          </div>
                          <span className="font-medium">Profile</span>
                        </Link>

                        <Link
                          to="/changepassword"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-xs sm:text-sm text-slate-700 hover:text-[#115B9D] hover:bg-blue-50/70 rounded-xl transition-all group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                            <GlobeLock className="h-4 w-4" />
                          </div>
                          <span className="font-medium">Change Password</span>
                        </Link>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-slate-100 my-1" />

                      {/* Logout Option */}
                      <div className="p-1.5 pt-0">
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            Logout();
                          }}
                          className="flex w-full items-center gap-3 px-3 py-2 text-xs sm:text-sm text-rose-600 hover:bg-rose-50 rounded-xl transition-all group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center shrink-0 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                            <LucideLogOut className="h-4 w-4" />
                          </div>
                          <span className="font-semibold">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hamburger — mobile */}
              <button
                className={`${hostname === subDomain && isHomePage && !isScrolled ? "text-white hover:text-[#116DAE]" : "text-gray-600 hover:text-[#116DAE]"} lg:hidden transition-colors p-1`}
                onClick={() => setMobileMenuOpen(true)}
              >
                <IoIosMenu size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 z-[99] bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${mobileMenuOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 left-0 bg-white z-[99] h-full w-[75%] max-w-[300px] flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b bg-white">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              {user?.image && !user.image.includes("placeholder") ? (
                <img
                  src={methodModel.userImg(user?.image)}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#115B9D] to-[#2590e0] text-white font-bold text-xs flex items-center justify-center ring-2 ring-slate-100 uppercase">
                  {getUserInitials()}
                </div>
              )}
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-800 capitalize text-sm truncate">
                {getUserDisplayName()}
              </h2>
              <span className="text-[10px] font-medium text-slate-400 capitalize">
                {getUserRoleLabel()}
              </span>
            </div>
          </div>
          <button
            className="text-gray-600 hover:text-gray-400 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={22} />
          </button>
        </div>

        {!isTenantAdminUser && !isProductsPage && (
          <div className="px-4 pt-4 pb-2">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setTimeout(() => setSearchOpen(true), 150);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#F5F8FC] border border-[#EAF1F9] text-gray-400 text-sm hover:border-[#116DAE] transition-colors"
            >
              <Search size={16} className="text-[#116DAE]" />
              <span>Search products...</span>
            </button>
          </div>
        )}

        <nav className="flex-1 px-4 py-4 flex flex-col gap-1 overflow-y-auto scrollbar">
          {navLinks
            .filter(({ label }) => {
              if (isTenantAdminUser) {
                if (
                  label === "Browse Products" ||
                  label === "Home" ||
                  label === "About" ||
                  label === "My Documents" ||
                  label === "Track Order"
                ) {
                  return false;
                }
                if (label === "Dashboard" || label === "Plans & Subscription") {
                  return true;
                }
              } else if (tenantInfo.isTenant) {
                if (label === "Home" || label === "About") {
                  return false;
                }
              }

              if (hostname === subDomain && label === "Browse Products") {
                return false;
              }

              if (!user?._id) {
                return !label.includes("Dashboard") && label !== "My Documents";
              }

              return label !== "Track Order";
            })
            .map(({ label, icon: Icon, href }, i) => (
              <button
                key={label}
                className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-lg font-medium text-left
          ${isActive(href)
                    ? "bg-blue-50 text-[#116DAE]"
                    : href
                      ? "text-gray-700 hover:bg-blue-50 hover:text-[#116DAE]"
                      : "text-gray-600 cursor-default"
                  }`}
                style={{ animationDelay: `${i * 60}ms` }}
                onClick={() => {
                  if (href) {
                    setMobileMenuOpen(false);
                    window.location.href = href;
                  }
                }}
                disabled={!href}
              >
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center group-hover:bg-blue-100 ${isActive(href)
                    ? "bg-blue-100 text-[#116DAE]"
                    : "bg-blue-50 text-[#116DAE]"
                    }`}
                >
                  <Icon size={16} />
                </span>
                {label}
              </button>
            ))}
          {!isTenantAdminUser && (
            <button
              className="group flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-lg font-medium text-left text-gray-700 hover:bg-blue-50 hover:text-[#116DAE]"
              onClick={() => {
                setMobileMenuOpen(false);
                openModal();
              }}
            >
              <span className="w-8 h-8 rounded-lg flex items-center justify-center group-hover:bg-blue-100 bg-blue-50 text-[#116DAE]">
                <FileText size={16} />
              </span>
              Quick Quote
            </button>
          )}
        </nav>

        <div className="flex flex-wrap justify-between gap-2 px-5 py-5 border-t">
          {!(user?.id || user?._id) ? (
            <>
              <Link to="/login" className="w-[48%]">
                <button className="w-full lg:flex items-center gap-2 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full text-sm shadow hover:scale-[1.04] duration-300 transition px-5 py-2">
                  Login
                </button>
              </Link>
              <Link to="/signup" className="w-[48%]">
                <button className="w-full lg:flex items-center gap-2 text-gray-600 border rounded-full text-sm shadow hover:scale-[1.04] duration-300 transition px-5 py-2">
                  Signup
                </button>
              </Link>
            </>
          ) : (
            <div className="w-full">
              <button
                onClick={() => Logout()}
                className="w-full lg:flex items-center gap-2 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full text-sm shadow hover:scale-[1.04] duration-300 transition px-5 py-2"
              >
                Logout
              </button>
            </div>
          )}
          {/* <button
            className="w-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full py-3 text-sm font-semibold shadow hover:opacity-90 transition flex items-center justify-center gap-2"
            onClick={() => { setIsOpen(true); setMobileMenuOpen(false); }}
          >
            <FileText size={16} />
            Request Quote
          </button> */}
        </div>
      </div>

      {!isTenantAdminUser && <FloatingCart />}
      <QuoteRequestModal
        open={isOpen1}
        setOpen={setIsOpen}
        onSuccess={() => console.log("submitted")}
      />
      <AIProductModal isOpen={isOpen} onClose={closeModal} detail={detail} />
    </>
  );
}
